/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import { TApiParams, TParams } from '@global-types/index';
import { getTechnologyProject } from '@utils/project-technologies';
import { getCountryName } from '@utils/countries';
import {
	formatCurrency,
	formatTokenAvailable,
} from '@utils/formats';
import { convertGCO2EToTCO2EWithoutUnit } from '@utils/functions';
import { TMesurabilityProject } from '@features/resources/measurabilities/types';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
export type TApiResponse = {
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
	rows: TMesurabilityProject[];
};

export type TParsedMesurabilityProject = {
	supportedBy?: string;
	entity: string;
	name: string;
	unit: string;
	technology: string;
	__t: string;
	sellPrice_europertco2eq: number;
	costPrice_europertco2eq: number;
	quantity: {
		total_aco2: number;
		held_aco2: number;
		burnt_aco2: number;
		remaining_aco2: number;
		unprocessed_gco2eq: number;
	};
	organization: string;
	location: {
		countryCode: string;
		place: string;
	};
	country?: {
		name: string;
		code: string;
	};
	region?: string;
	type?: string;
	createdAt: string;
	updatedAt: string;
	id: string;
	tokenAvailable?: string;
	quantitytco2eq?: number;
	cover?: string;
	priceTco2?: string;
	image?: string;
	isDisabled: boolean;
	active: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getMeasurabilityProjects = (
	params: TApiParams
): Promise<TApiResponse> => {
	const { queryParams, page, pageSize } = params;
	const { name, ...restQueryParams } = queryParams;
	return apiClient.get('/measurabilities', {
		params: {
			page: page,
			pageSize: pageSize,
			query: restQueryParams,
			sort: '-supportedBy -active -quantity.remaining_aco2 name',
			...(name && {
				search: name,
				searchFields: ['name', 'location.place'],
			}),
			populate:
				'cover, logo, publicSpecs, supportedBy.logo',
		},
	});
};

export const getPublicMeasurabilityProjects = (
	params: TApiParams
): Promise<TApiResponse> => {
	const { queryParams, page, pageSize } = params;
	const { name, ...restQueryParams } = queryParams;
	return apiClient.get('/public/measurabilities', {
		params: {
			page: page,
			pageSize: pageSize,
			query: restQueryParams,
			active: true,
			sort: '-supportedBy -active -quantity.remaining_aco2 name',
			...(name && {
				search: name,
				searchFields: ['name', 'location.place'],
			}),
			populate:
				'cover, logo, publicSpecs, supportedBy.logo',
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useMeasurabilityProjects = (
	page: number,
	size: number,
	queryParams: TParams
) => {
	const { checkAcl, checkRole } = useAuthorization();

	const { t } = useTranslation();

	const { data, isFetching, isFetched } = useQuery({
		queryKey: [
			'measurability-projects',
			{
				page: page,
				size: size,
				queryParams: queryParams,
			},
		],
		queryFn: () => {
			if (
				!checkRole([
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.PRODUCER_ADMIN,
					ROLES.PRODUCER_STAFF,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.ORGANIZATION_STAFF,
				]) &&
				!checkAcl(ACLS.MEASURABILITIES.LIST)
			) {
				return getPublicMeasurabilityProjects({
					page: page,
					pageSize: size,
					queryParams: queryParams,
				});
			}
			return getMeasurabilityProjects({
				page: page,
				pageSize: size,
				queryParams: queryParams,
			});
		},
		staleTime: 0,
	});

	const updateList = (projects: TMesurabilityProject[]) =>
		projects.reduce(
			(
				acc: TParsedMesurabilityProject[],
				currentItem: TMesurabilityProject
			) => {
				const technolgyProject = getTechnologyProject(
					currentItem.technology
				);
				const countryName = getCountryName(
					currentItem.location.countryCode
				);

				const cover =
					currentItem && currentItem.cover
						? currentItem.cover
						: technolgyProject
							? technolgyProject.image
							: '';

				const modifiedItem: TParsedMesurabilityProject = {
					id: currentItem.id,
					name: currentItem.name,
					country: {
						name: countryName,
						code: currentItem.location.countryCode,
					},
					region: currentItem.location.place,
					type: technolgyProject
						? technolgyProject.label
						: '',
					priceTco2: formatCurrency(
						currentItem.sellPrice_europertco2eq,
						t('global.sellPriceUnit')
					),

					tokenAvailable:
						currentItem.quantity &&
						currentItem.quantity.remaining_aco2
							? formatTokenAvailable(
									convertGCO2EToTCO2EWithoutUnit(
										currentItem.quantity.remaining_aco2
									)
								)
							: '0',

					image: cover,
					quantitytco2eq: 0,
					sellPrice_europertco2eq:
						currentItem.sellPrice_europertco2eq,
					isDisabled:
						currentItem.quantity &&
						currentItem.quantity.remaining_aco2
							? formatTokenAvailable(
									convertGCO2EToTCO2EWithoutUnit(
										currentItem.quantity.remaining_aco2
									)
								) == '0'
							: false,
					entity: '',
					unit: '',
					technology: '',
					__t: '',
					costPrice_europertco2eq: 0,
					quantity: {
						total_aco2: 0,
						held_aco2: 0,
						burnt_aco2: 0,
						remaining_aco2: 0,
						unprocessed_gco2eq: 0,
					},
					organization: '',
					location: {
						countryCode: '',
						place: '',
					},
					createdAt: '',
					updatedAt: '',
					supportedBy: currentItem?.supportedBy?.logo,
					active: currentItem.active,
				};
				acc.push(modifiedItem);

				return acc;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetching,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
